import dayjs from 'dayjs'
import { expressURL } from '../../../../../services/api'

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export const exportToZoho = async ({
  invoiceDetails,
  state,
  invoiceDate,
  totals,
  energyValu,
  mainEnergy,
  mainTariff,
  deemedEnergy,
  deemedTariff,
  omAmounts,
  zohoState,
  zohoinvoicenumber,
}) => {
  try {
    if (!invoiceDetails || invoiceDetails.length === 0) {
      throw new Error('Please save the invoice before exporting to Zoho')
    }

    // Check if already sent to Zoho
    if (invoiceDetails[0].sentToZoho === true) {
      throw new Error('Invoice already exported to Zoho')
    }

    const currentInvoice = invoiceDetails[0]
    // Prefer dates from DB (currentInvoice) when available. Fall back to invoiceDate param.
    const dbInvoiceDate =
      currentInvoice.invoiceDate || currentInvoice.sentDate || invoiceDate
    const dbDueDate = currentInvoice.dueDate || invoiceDate

    const CurrentMonth = new Date(dbInvoiceDate).getMonth()
    const currentMonth = months[CurrentMonth]
    const currentYear = dayjs(dbInvoiceDate).format('YYYY')
    const url = `${expressURL}`

    // Determine whether organization GSTIN is present
    const hasGSTN =
      zohoState?.organizationGstNo &&
      zohoState.organizationGstNo.trim() !== '' &&
      zohoState.organizationGstNo !== 'N/A'

    // HSN/SAC code for energy services (solar power generation)
    const HSN_SAC_CODE = '27160000'

    // Helper function to create Zoho-compatible invoice number
    const createZohoCompatibleNumber = (originalNumber) => {
      if (!originalNumber) return currentInvoice.invoiceNumber

      // Remove invalid characters and limit to 16 characters
      let validNumber = originalNumber.replace(/[^a-zA-Z0-9\-\/]/g, '')

      // If too long, try to shorten intelligently
      if (validNumber.length > 16) {
        const parts = validNumber.split(/[\-\/]/)
        if (parts.length >= 3) {
          const prefix = parts[0] // INV
          const month = parts[1] // FEB
          const year = parts[2].slice(-2) // 25 (last 2 digits of year)
          const number = parts[parts.length - 1] // 001 (last part)

          validNumber = `${prefix}-${month}${year}-${number}`
        }

        // If still too long, truncate
        if (validNumber.length > 16) {
          validNumber = validNumber.substring(0, 16)
        }
      }

      return validNumber
    }

    // Create a Zoho-compatible invoice number
    const zohoCompatibleNumber = createZohoCompatibleNumber(zohoinvoicenumber)

    console.log('Original invoice number:', zohoinvoicenumber)
    console.log('Zoho-compatible number:', zohoCompatibleNumber)

    const lineItems = []

    // Helper function to create line item with proper tax handling
    const createLineItem = (
      name,
      description,
      rate,
      quantity,
      unit = 'kWh',
      hsnSacCode
    ) => {
      const lineItem = {
        name,
        description,
        rate: parseFloat(rate || 0),
        quantity: parseFloat(quantity || 0),
        unit,
        item_type: 'service',
      }

      // Add HSN/SAC only when provided (and when GSTIN present)
      if (hsnSacCode) {
        lineItem.hsn_or_sac = hsnSacCode
      }

      // FIXED: Always specify tax exemption for zero-rate items or apply tax for non-zero items
      const itemAmount = lineItem.rate * lineItem.quantity

      if (itemAmount === 0 || lineItem.rate === 0) {
        // For zero-amount items, use tax exemption
        lineItem.tax_exemption_id = 'tax_exempt' // Use appropriate tax exemption ID
        lineItem.tax_percentage = 0
      } else {
        // For non-zero items, determine tax based on location
        const isLocationMatched = zohoState?.isLocationMatched || false

        if (isLocationMatched) {
          // Same state - use CGST + SGST (9% + 9% = 18%)
          lineItem.tax_percentage = 0
          lineItem.cgst_percentage = 0
          lineItem.sgst_percentage = 0
        } else {
          // Different state - use IGST (18%)
          lineItem.tax_percentage = 0
          lineItem.igst_percentage = 0
        }
      }

      return lineItem
    }

    // Add main energy line item with HSN/SAC code
    if (parseFloat(energyValu || mainEnergy || 0) > 0) {
      lineItems.push(
        createLineItem(
          `${state.data.plantName} (${state.data.plantSize || '17kWp'})`,
          `Energy Generation for ${currentMonth} ${currentYear}`,
          mainTariff || 0,
          energyValu || mainEnergy || 0,
          'kWh',
          hasGSTN ? HSN_SAC_CODE : undefined
        )
      )
    }

    // Add deemed generation line item with HSN/SAC code
    if (parseFloat(deemedEnergy || 0) > 0) {
      lineItems.push(
        createLineItem(
          'Deemed Generation',
          `Deemed Energy for ${currentMonth} ${currentYear}`,
          deemedTariff || 0,
          deemedEnergy || 0,
          'kWh',
          hasGSTN ? HSN_SAC_CODE : undefined
        )
      )
    } else {
      // Add zero quantity deemed generation item with tax exemption
      lineItems.push(
        createLineItem(
          'Deemed Generation',
          `Deemed Energy for ${currentMonth} ${currentYear}`,
          0.0,
          0, // Changed from 0.0 to 1 to show line item
          'kWh',
          hasGSTN ? HSN_SAC_CODE : undefined
        )
      )
    }

    // Add O&M charges if applicable (different SAC code for maintenance services)
    if (
      parseFloat(omAmounts || 0) > 0 &&
      state?.data?.proposalType?.type === 'EMI'
    ) {
      lineItems.push(
        createLineItem(
          'O&M Charges',
          `Operations & Maintenance charges for ${currentMonth} ${currentYear}`,
          omAmounts || 0,
          1,
          'month',
          hasGSTN ? '998314' : undefined // SAC code for maintenance and repair services (only when GSTIN present)
        )
      )
    }

    console.log(
      '📋 Line items with tax configuration and HSN/SAC codes:',
      lineItems
    )

    // FIXED: Simplified notes
    const simplifiedNotes = `Subject: Summary of Charges for ${currentMonth} ${currentYear}`

    // Build payload and add HSN/SAC configuration only when applicable
    const payload = {
      invoice: {
        _id: currentInvoice._id,
        customer_name: state.data.clientDetails?.name || state.data.plantName,
        invoice_number: currentInvoice.invoiceNumber,
        custom_invoice_number: zohoCompatibleNumber,
        force_custom_number: true,
        // Use invoice date and due date from DB (if present), formatted as YYYY-MM-DD
        date: dayjs(dbInvoiceDate).format('YYYY-MM-DD'),
        due_date: dayjs(dbDueDate).format('YYYY-MM-DD'),
        line_items: lineItems,
        subject: `Summary of Charges for ${currentMonth} ${currentYear}`,
        // FIXED: Use simplified notes
        notes: simplifiedNotes,
        // Add tax configuration details
        tax_configuration: {
          is_location_matched: zohoState?.isLocationMatched || false,
        },
        bank_details: {
          account_name: state.data.bankDetails?.accountName || 'AXIS Bank',
          account_number:
            state.data.bankDetails?.accountNumber || '922020010026345',
          bank_name: state.data.bankDetails?.bankName || 'AXIS Bank',
          ifsc_code: state.data.bankDetails?.ifscCode || 'UTIB0003702',
          bank_address: state.data.bankDetails?.bankAddress || 'ELDAMS Road',
        },
        totals: {
          current_charges: totals.totalAmount,
          outstanding_balance: totals.outstandingTotal,
          balance_due: totals.outstandingTotal + totals.totalAmount,
          total_billable_units: parseFloat(energyValu || mainEnergy || 0),
        },
      },
    }

    if (hasGSTN) {
      payload.invoice.tax_configuration = {
        energy_services: HSN_SAC_CODE,
        maintenance_services: '998314',
      }
    }

    const token = localStorage.getItem('accessToken')
    const response = await fetch(`${expressURL}/zoho/invoices/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(payload),
    })

    const result = await response.json()

    if (!result.success) {
      throw new Error(result.error || 'Unknown error occurred')
    }

    return {
      success: true,
      message: 'Invoice exported to Zoho Books successfully!',
      data: result,
    }
  } catch (error) {
    console.error('Error exporting to Zoho:', error)
    return {
      success: false,
      message: error.message || 'Error exporting to Zoho',
      error: error,
    }
  }
}

export const getZohoInvoiceDetails = async (organizationId) => {
  console.log('Fetching Zoho invoice details for organization:', organizationId)

  try {
    const url = `${expressURL}`
    const token = localStorage.getItem('accessToken')
    const response = await fetch(
      `${url}/zoho/data?organizationId=${organizationId}`,
      {
        method: 'GET',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    )

    if (!response.ok) {
      throw new Error('Failed to fetch invoice details from Zoho')
    }

    const data = await response.json()
    console.log('Zoho invoice details fetched successfully:', data)
    return data
  } catch (error) {
    console.error('Error fetching Zoho invoice details:', error)
    throw error
  }
}
